import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { getPropertyList } from '../../Inventory/pages/FacilityInventory/Helpers/Api';
import { setLoading } from '../../../store/actions';

import { PropertySelector } from './Components/PropertySelector';
import { PreMoveoutListTable } from './Components/PreMoveoutListTable';
import { PreMoveoutFormModal } from './Components/PreMoveoutFormModal';
import { PreMoveoutDetailModal } from './Components/PreMoveoutDetailModal';
import { AssignVendorModal } from './Components/AssignVendorModal';
import { TenantChecklistModal } from './Components/TenantChecklistModal';
import { listPreMoveoutInspections, getPreMoveoutUnits } from './Helpers/Api';
import { decodeUser, REPAIR_CATEGORIES } from './Helpers/format';
import './pre-moveout.scss';

/**
 * Staff page: list of pre-moveout inspections for a property/building, with a
 * full-screen create/edit form and a detail modal (vendors, quotes, approvals).
 */
const PreMoveoutInspection = () => {
  const dispatch = useDispatch();
  const [user] = useState(() => decodeUser());
  const readOnly = String(user?.role || '').toLowerCase() === 'auditor';

  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ property_id: '', building_name: '', status: 'all' });
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [entryMode, setEntryMode] = useState(() => { try { return localStorage.getItem('pm-entry-mode') === 'modal' ? 'modal' : 'inline'; } catch { return 'inline'; } });
  const [assign, setAssign] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [categories, setCategories] = useState(REPAIR_CATEGORIES);
  const [listLoading, setListLoading] = useState(false);
  const listRequest = useRef(0);

  const [form, setForm] = useState({ open: false, mode: 'create', doc: null });
  const [detail, setDetail] = useState({ open: false, id: null });
  const [detailRefresh, setDetailRefresh] = useState(0);

  const currentProperty = useMemo(
    () => properties.find((p) => p._id === filters.property_id) || null,
    [properties, filters.property_id]
  );
  const propertyHasBuildings = useMemo(
    () => (currentProperty?.building || []).some((b) => b && b.buildingName),
    [currentProperty]
  );
  const selectionReady = !!filters.property_id && (!propertyHasBuildings || !!filters.building_name);

  /* ------------------------------------------------ data */
  const fetchProperties = useCallback(async () => {
    if (!user) return;
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(user.role || '', user.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, user]);

  const fetchList = useCallback(async () => {
    const request = ++listRequest.current;
    if (!selectionReady) {
      setItems([]);
      setUnits([]);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const [{ data, meta }, unitRows] = await Promise.all([listPreMoveoutInspections({
        propertyId: filters.property_id,
        buildingName: filters.building_name || undefined,
      }), getPreMoveoutUnits(filters.property_id, filters.building_name)]);
      if (request !== listRequest.current) return;
      setUnits(unitRows);
      setItems(Array.isArray(data) ? data : []);
      if (Array.isArray(meta?.categories) && meta.categories.length) setCategories(meta.categories);
    } catch (err) {
      if (request !== listRequest.current) return;
      setItems([]);
      setUnits([]);
      toast.error(err.message || 'Failed to load pre-moveout inspections');
    } finally {
      if (request === listRequest.current) setListLoading(false);
    }
  }, [selectionReady, filters.property_id, filters.building_name]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    const requests = listRequest;
    fetchList();
    return () => { requests.current++; };
  }, [fetchList]);

  /* ------------------------------------------------ handlers */
  const openCreate = (unit = '') => {
    if (!selectionReady) return;
    if (form.open) { toast.info('Save or close the open inspection first.'); return; }
    setForm({ open: true, mode: 'create', doc: null, unit: typeof unit === 'string' ? unit : '' });
  };

  const openEdit = (doc) => { if (form.open) { toast.info('Save or close the open inspection first.'); return; } closeDetail(); setForm({ open: true, mode: 'edit', doc, unit: doc.unit_number }); };

  const closeForm = () => setForm((f) => ({ ...f, open: false }));

  const handleSaved = useCallback(
    (doc) => {
      fetchList();
      if (doc?._id) {
        setDetailRefresh((n) => n + 1);
      }
    },
    [fetchList]
  );

  const openDetail = (doc) => setDetail({ open: true, id: doc._id });
  const closeDetail = () => setDetail((d) => ({ ...d, open: false }));

  const handleDeleted = () => {
    closeDetail();
    fetchList();
  };

  const renderForm = (inline) => <PreMoveoutFormModal isOpen={form.open} onClose={closeForm} mode={form.mode} doc={form.doc} initialUnit={form.unit || ''} inline={inline} propertyId={filters.property_id} buildingName={filters.building_name} propertyTitle={currentProperty?.title || ''} categories={categories} onSaved={handleSaved} />;

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Pre-Moveout Inspections</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Building Operations" breadcrumbItem="Pre-Moveout Inspections" />

        <PropertySelector propertyList={properties} filters={filters} onChange={(next) => { if (form.open) { toast.info('Save or close the open inspection before changing the selection.'); return; } if (next.property_id !== filters.property_id || next.building_name !== filters.building_name) { listRequest.current++; setItems([]); setUnits([]); } setFilters(next); }} isLoading={listLoading}>
          {!readOnly && (
            <Button
              color="primary"
              onClick={openCreate}
              disabled={!selectionReady}
              title={selectionReady ? 'Record a new pre-moveout inspection' : 'Choose a property and building first'}
              style={{ minHeight: 38 }}
            >
              <FeatherIcon icon="plus" size={16} className="me-1" />
              New inspection
            </Button>
          )}
        </PropertySelector>

        <div className="pm-entry-toolbar">
          <div><FeatherIcon icon="clipboard" size={18} className="me-2" /><strong>Prepare the unit for its next resident</strong><p className="text-muted mb-0 small">Inspect, request a quote, approve and confirm the repair appointment.</p></div>
          <div className="btn-group" role="group" aria-label="Inspection entry mode">{['inline', 'modal'].map(value => <Button key={value} size="sm" color={entryMode === value ? 'primary' : 'light'} aria-pressed={entryMode === value} disabled={form.open} onClick={() => { setEntryMode(value); try { localStorage.setItem('pm-entry-mode', value); } catch {} }}><FeatherIcon icon={value === 'inline' ? 'list' : 'maximize-2'} size={14} className="me-1" />{value === 'inline' ? 'Inline entry' : 'Modal entry'}</Button>)}</div>
        </div>
        {form.open && entryMode === 'inline' && !form.unit && renderForm(true)}
        <PreMoveoutListTable items={items} units={units} loading={listLoading} hasSelection={selectionReady} status={filters.status} onRowClick={openDetail} onCreate={openCreate} onEdit={openEdit} onAssign={setAssign} onChecklist={setChecklist} readOnly={readOnly} inlineUnit={form.open && entryMode === 'inline' ? form.unit : null} renderInline={() => renderForm(true)} />
        {entryMode === 'modal' && renderForm(false)}
        <AssignVendorModal isOpen={!!assign} report={assign} onClose={() => setAssign(null)} onAssigned={handleSaved} />
        <TenantChecklistModal unit={checklist} propertyId={filters.property_id} buildingName={filters.building_name} propertyTitle={currentProperty?.title || ''} onClose={() => setChecklist(null)} />

        <PreMoveoutDetailModal
          isOpen={detail.open}
          onClose={closeDetail}
          reportId={detail.id}
          refreshToken={detailRefresh}
          onChanged={fetchList}
          onEdit={openEdit}
          onDeleted={handleDeleted}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export { PreMoveoutInspection };
export default PreMoveoutInspection;
